import React, { useCallback, useState } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { TrackRow } from "@/components/ripeness/TrackRow";
import { fmtMoney } from "@/components/ripeness/radarMeta";

/** Σ expected-value midpoints over every matching track (not just the page). */
function ValueTotal({ vt }) {
    if (!vt) return null;
    return (
        <span className="text-sm text-slate-300" data-testid="ripeness-value-total"
            title={`Σ expected ${fmtMoney(vt.expected_low)}–${fmtMoney(vt.expected_high)} · Σ if awarded ${fmtMoney(vt.if_awarded_mid)} · ${vt.tracks_valued} of ${vt.tracks_total} tracks valued`}>
            {" · "}Σ expected <b className="text-emerald-200" data-testid="ripeness-value-total-mid">{fmtMoney(vt.expected_mid, vt.currency)}</b>
            {vt.has_weights ? <>{" · "}Σ value to us <b className="text-violet-200" data-testid="ripeness-value-total-weighted">{fmtMoney(vt.weighted_mid, vt.currency)}</b></> : null}
            <span className="text-xs text-slate-500"> ({fmtMoney(vt.expected_low)}–{fmtMoney(vt.expected_high)} · {vt.tracks_valued} valued · {vt.phase === "best" ? "best phase" : `P${vt.phase}`})</span>
        </span>
    );
}

/** Track list with one expandable drill-down. `onExclude` / `onRestore`
 *  must resolve to `true` when they changed something — the open row is
 *  collapsed and the list refreshed by the caller in that case. */
export function RipenessTable({ tracks, totalTracks, valueTotal, loading, onExclude, onRestore }) {
    const [expandedTrack, setExpandedTrack] = useState(null);
    const [expandedDetail, setExpandedDetail] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);

    const collapse = useCallback(() => { setExpandedTrack(null); setExpandedDetail(null); }, []);

    const toggleTrack = useCallback(async (t) => {
        if (expandedTrack === t.track_id) { collapse(); return; }
        setExpandedTrack(t.track_id);
        setExpandedDetail(null);
        setLoadingDetail(true);
        try {
            const r = await api.get(`/v1/ripeness/tracks/${encodeURIComponent(t.track_id)}`);
            setExpandedDetail(r.data);
        } catch (e) {
            toast.error(`Track detail failed: ${formatApiErrorDetail(e)}`);
            setExpandedDetail({ track: t, evaluation: null, evidence: { registry: [], articles: [] } });
        } finally {
            setLoadingDetail(false);
        }
    }, [expandedTrack, collapse]);

    const reloadDetail = useCallback(async () => {
        if (!expandedTrack) return;
        try {
            const r = await api.get(`/v1/ripeness/tracks/${encodeURIComponent(expandedTrack)}`);
            setExpandedDetail(r.data);
        } catch (e) { /* keep the current detail */ }
    }, [expandedTrack]);

    const exclude = useCallback(async (t) => { if (await onExclude(t)) collapse(); }, [onExclude, collapse]);
    const restore = useCallback(async (x) => { if (await onRestore(x)) collapse(); }, [onRestore, collapse]);

    return (
        <div className="rounded-lg border border-slate-700/60 bg-slate-900/40 p-4" data-testid="ripeness-table">
            <div className="flex items-center justify-between mb-3">
                <div className="text-sm text-slate-300" data-testid="ripeness-table-summary">
                    Showing <b>{tracks.length}</b> of <b>{totalTracks}</b> tracks<ValueTotal vt={valueTotal} />
                </div>
                {loading ? (
                    <span className="text-xs text-slate-400 inline-flex items-center gap-1.5" data-testid="ripeness-table-loading">
                        <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Loading…
                    </span>
                ) : null}
            </div>
            {tracks.length === 0 ? (
                <div className="text-center py-10 text-slate-400" data-testid="ripeness-table-empty">
                    <AlertTriangle className="w-8 h-8 mx-auto text-amber-400 mb-2" />
                    <div>No tracks yet.</div>
                    <div className="text-xs mt-1">Run a backfill from the button above.</div>
                </div>
            ) : (
                <div>
                    {tracks.map((t) => (
                        <TrackRow
                            key={t.track_id}
                            track={t}
                            expanded={expandedTrack === t.track_id}
                            onToggle={() => toggleTrack(t)}
                            detail={expandedTrack === t.track_id ? expandedDetail : null}
                            loadingDetail={expandedTrack === t.track_id && loadingDetail}
                            onExclude={exclude}
                            onRestore={restore}
                            onRescored={reloadDetail}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
